import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabaseHelpers } from '../../../lib/supabase';
import { useToast } from '../../../contexts/ToastContext';

const FilamentHistoryModal = ({ isOpen, onClose, filament }) => {
  const { error } = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('usage');

  useEffect(() => {
    if (isOpen && filament?.id) {
      loadHistory();
    }
  }, [isOpen, filament]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseHelpers?.getFilamentUsageHistory(filament?.id);
      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalUsed = history?.reduce((sum, record) => sum + (parseFloat(record?.weight_used) || 0), 0);
  const totalCost = history?.reduce((sum, record) => sum + (parseFloat(record?.cost_at_time) || 0), 0);
  const remainingWeight = parseFloat(filament?.currentWeight || 0);
  const originalWeight = parseFloat(filament?.originalWeight || 0);
  const usagePercentage = originalWeight > 0 ? ((originalWeight - remainingWeight) / originalWeight) * 100 : 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div 
                  className="w-12 h-12 rounded-xl border-2 border-border shadow-inner flex-shrink-0"
                  style={{ backgroundColor: filament?.colorHex || '#ffffff' }}
                />
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    Histórico do Filamento
                  </h2>
                  <p className="text-muted-foreground">
                    {filament?.brand} {filament?.material} {filament?.colorName}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
              />
            </div>
          </div>

          {/* Stats Overview */}
          <div className="p-6 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{originalWeight}g</div>
                <div className="text-sm text-muted-foreground">Peso Original</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{totalUsed?.toFixed(1)}g</div>
                <div className="text-sm text-muted-foreground">Total Usado</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-success">{remainingWeight}g</div>
                <div className="text-sm text-muted-foreground">Restante</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary">R$ {totalCost?.toFixed(2)}</div>
                <div className="text-sm text-muted-foreground">Custo Usado</div>
              </div>
            </div>

            {/* Usage Bar */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-foreground">Progresso de Uso</span>
                <span className="text-sm text-muted-foreground">{usagePercentage?.toFixed(1)}% usado</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('usage')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'usage' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Projects" size={16} className="inline mr-2" />
              Histórico de Uso ({history?.length})
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'details' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Info" size={16} className="inline mr-2" />
              Detalhes do Material
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto max-h-96">
            {activeTab === 'usage' && (
              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-muted-foreground">Carregando histórico...</span>
                    </div>
                  </div>
                ) : history?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="FileX" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Nenhum uso registrado
                    </h3>
                    <p className="text-muted-foreground">
                      Este filamento ainda não foi usado em nenhum projeto.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history?.map((record, index) => (
                      <motion.div
                        key={record?.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-card border border-border rounded-lg p-4 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <Icon name="Projects" size={16} className="text-primary" />
                              <h4 className="font-semibold text-foreground">
                                {record?.project?.name || 'Projeto sem nome'}
                              </h4>
                              <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                                {formatDate(record?.usage_date)}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Peso usado:</span>
                                <span className="font-medium text-foreground ml-2">
                                  {parseFloat(record?.weight_used)?.toFixed(1)}g
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Custo:</span>
                                <span className="font-medium text-foreground ml-2">
                                  R$ {parseFloat(record?.cost_at_time)?.toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Custo/g:</span>
                                <span className="font-medium text-foreground ml-2">
                                  R$ {(parseFloat(record?.cost_at_time) / parseFloat(record?.weight_used))?.toFixed(4)}
                                </span>
                              </div>
                            </div>

                          {record?.notes && (
                            <div className="mt-2 text-sm text-muted-foreground bg-muted/50 rounded p-2">
                              <Icon name="FileText" size={14} className="inline mr-1" />
                              {record?.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Imagens do projeto e upload */}
                      <div className="mt-3">
                        {Array.isArray(record?.project?.image_urls) && record?.project?.image_urls?.length > 0 && (
                          <div className="flex gap-2 flex-wrap mb-2">
                            {record?.project?.image_urls?.slice(0, 6)?.map((url, idx) => (
                              <div key={`${record?.id}-img-${idx}`} className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                                <img src={url} alt="Imagem do projeto" className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <input
                            id={`upload-${record?.id}`}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e?.target?.files || []);
                              if (!files?.length) return;
                              try {
                                const newUrls = [];
                                for (const f of files) {
                                  const { publicUrl } = await supabaseHelpers?.uploadProjectImage?.(f, record?.project?.id);
                                  if (publicUrl) newUrls.push(publicUrl);
                                }
                                if (newUrls?.length > 0) {
                                  const next = [...(record?.project?.image_urls || []), ...newUrls];
                                  try { await supabaseHelpers?.updateProject?.(record?.project?.id, { image_urls: next }); } catch {}
                                  setHistory(prev => prev?.map(r => r?.id === record?.id ? ({
                                    ...r,
                                    project: { ...(r?.project || {}), image_urls: next }
                                  }) : r));
                                }
                              } catch (err) {
                                console.error('Erro no upload de imagem do projeto:', err);
                                error('Não foi possível enviar as imagens do projeto.');
                              } finally {
                                try { e.target.value = ''; } catch {}
                              }
                            }}
                          />
                          <label htmlFor={`upload-${record?.id}`}>
                            <Button variant="outline" size="xs" iconName="ImagePlus" iconPosition="left">
                              Adicionar imagens do projeto
                            </Button>
                          </label>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
            )}

            {activeTab === 'details' && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Filament" size={16} />
                      <span>Informações do Material</span>
                    </h4>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Marca:</span>
                        <span className="font-medium text-foreground">{filament?.brand}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Material:</span>
                        <span className="font-medium text-foreground">{filament?.material}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cor:</span>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-foreground">{filament?.colorName}</span>
                          <div 
                            className="w-4 h-4 rounded border border-border"
                            style={{ backgroundColor: filament?.colorHex }}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Diâmetro:</span>
                        <span className="font-medium text-foreground">{filament?.diameter || '1.75'}mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="DollarSign" size={16} />
                      <span>Informações de Custo</span>
                    </h4>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Custo total:</span>
                        <span className="font-medium text-foreground">R$ {parseFloat(filament?.totalCost || 0)?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Custo por grama:</span>
                        <span className="font-medium text-foreground">R$ {parseFloat(filament?.costPerGram || 0)?.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Data da compra:</span>
                        <span className="font-medium text-foreground">
                          {new Date(filament?.purchaseDate || filament?.createdAt)?.toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Valor restante:</span>
                        <span className="font-medium text-success">
                          R$ {(parseFloat(filament?.costPerGram || 0) * parseFloat(filament?.currentWeight || 0))?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {filament?.notes && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
                      <Icon name="FileText" size={16} />
                      <span>Observações</span>
                    </h4>
                    <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                      {filament?.notes}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border p-6">
            <div className="flex justify-end">
              <Button onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FilamentHistoryModal;